import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search, User } from 'lucide-react';
import ProductSelectorModal from './ProductSelectorModal';
import ClientAutocompleteInput from '../Client_Component/ClientAutocompleteInput';
import NewClientModal from '../NewClientModal';
import apiService from '../../services/api';

const NewQuotationModal = ({ quotation, onClose, onSave, products, productCategories }) => {
    const [formData, setFormData] = useState({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        projectName: '',
        validityDays: 30,
        items: [],
        status: 'pending',
        // Client integration fields
        selectedClientId: null,
        companyName: ''
    });

    const [showProductSelector, setShowProductSelector] = useState(false);
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [quickCreateClientName, setQuickCreateClientName] = useState('');

    useEffect(() => {
        if (quotation) {
            setFormData(quotation);
        }
    }, [quotation]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.clientName.trim()) {
            newErrors.clientName = 'Client name is required';
        }

        if (!formData.clientEmail.trim()) {
            newErrors.clientEmail = 'Client email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) {
            newErrors.clientEmail = 'Please enter a valid email address';
        }

        if (!formData.projectName.trim()) {
            newErrors.projectName = 'Project name is required';
        }

        if (formData.items.length === 0) {
            newErrors.items = 'At least one item is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // NEW: Handle client selection from autocomplete
    const handleClientSelect = (client) => {
        console.log('Selected client:', client);
        
        setFormData(prev => ({
            ...prev,
            selectedClientId: client.id,
            clientName: client.clientName,
            clientEmail: client.clientEmail,
            clientPhone: client.clientPhone || '',
            companyName: client.companyName || ''
        }));

        // Clear client-related errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.clientName;
            delete newErrors.clientEmail;
            return newErrors;
        });
    };

    // NEW: Handle creating a new client
    const handleCreateNewClient = (searchTerm) => {
        setQuickCreateClientName(searchTerm);
        setShowNewClientModal(true);
    };

    // NEW: Handle client creation success
    const handleClientCreated = async (clientData) => {
        try {
            const response = await apiService.createClient(clientData);
            
            if (response.success) {
                // Auto-select the newly created client
                handleClientSelect({
                    id: response.data._id,
                    label: clientData.companyName ? 
                        `${clientData.clientName} (${clientData.companyName})` : 
                        clientData.clientName,
                    clientName: clientData.clientName,
                    clientEmail: clientData.clientEmail,
                    clientPhone: clientData.clientPhone,
                    companyName: clientData.companyName
                });

                setShowNewClientModal(false);
                setQuickCreateClientName('');
                
                // Show success notification (you can replace with your notification system)
                alert('Client created successfully!');
            } else {
                alert('Failed to create client: ' + response.message);
            }
        } catch (error) {
            console.error('Error creating client:', error);
            alert('Failed to create client. Please try again.');
        }
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        if (field === 'quantity' || field === 'unitPrice') {
            // Make sure both values are numbers, default to 0 if NaN
            const quantity = Number(updatedItems[index].quantity) || 0;
            const unitPrice = Number(updatedItems[index].unitPrice) || 0;
            updatedItems[index].totalPrice = quantity * unitPrice;
        }

        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleAddProducts = (selectedProducts) => {
        const newItems = selectedProducts.map(product => {
            const item = {
                itemNo: product.productId || product.barcode || '', // Primary identifier
                barcode: product.barcode || '', // ✅ Store barcode separately if needed
                description: product.description || 'MISSING DESCRIPTION',
                quantity: 1,
                unitPrice: 0,
                totalPrice: 0
            };
            
            return item;
        });

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, ...newItems]
        }));

        setShowProductSelector(false);
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const calculateTotal = () => {
        return formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const quotationData = {
                ...formData,
                totalAmount: calculateTotal()
            };

            await onSave(quotationData);
        } catch (error) {
            console.error('Error saving quotation:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '1200px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#1f2937',
                        margin: 0
                    }}>
                        {quotation ? 'Edit Quotation' : 'New Quotation'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#6b7280'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                <div style={{
                    display: 'flex',
                    height: 'calc(90vh - 140px)'
                }}>
                    {/* Form Section */}
                    <div style={{
                        flex: '2',
                        padding: '24px',
                        overflowY: 'auto'
                    }}>
                        <form onSubmit={handleSubmit}>
                            {/* Client Information - UPDATED SECTION */}
                            <div style={{ marginBottom: '32px' }}>
                                <h3 style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <User size={20} />
                                    Client Information
                                </h3>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '16px'
                                }}>
                                    {/* UPDATED: Client Name with Autocomplete */}
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Client Name *
                                        </label>
                                        <ClientAutocompleteInput
                                            value={formData.clientName}
                                            onChange={(value) => handleInputChange('clientName', value)}
                                            onClientSelect={handleClientSelect}
                                            onCreateNewClient={handleCreateNewClient}
                                            placeholder="Search for existing client or enter new client name..."
                                        />
                                        {errors.clientName && (
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#dc2626',
                                                marginTop: '4px',
                                                margin: '4px 0 0 0'
                                            }}>
                                                {errors.clientName}
                                            </p>
                                        )}
                                    </div>

                                    {/* Company Name Display (if selected from existing client) */}
                                    {formData.companyName && (
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#374151',
                                                marginBottom: '6px'
                                            }}>
                                                Company
                                            </label>
                                            <div style={{
                                                padding: '12px',
                                                backgroundColor: '#f9fafb',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#6b7280'
                                            }}>
                                                {formData.companyName}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.clientEmail}
                                            onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${errors.clientEmail ? '#dc2626' : '#d1d5db'}`,
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="client@example.com"
                                        />
                                        {errors.clientEmail && (
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#dc2626',
                                                marginTop: '4px',
                                                margin: '4px 0 0 0'
                                            }}>
                                                {errors.clientEmail}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.clientPhone}
                                            onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="+961 1 234567"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Project Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.projectName}
                                            onChange={(e) => handleInputChange('projectName', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${errors.projectName ? '#dc2626' : '#d1d5db'}`,
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="Enter project name"
                                        />
                                        {errors.projectName && (
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#dc2626',
                                                marginTop: '4px',
                                                margin: '4px 0 0 0'
                                            }}>
                                                {errors.projectName}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Validity (Days)
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.validityDays}
                                            onChange={(e) => handleInputChange('validityDays', parseInt(e.target.value))}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            min="1"
                                            max="365"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Items Section - UNCHANGED */}
                            <div style={{ marginBottom: '32px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        margin: 0
                                    }}>
                                        Items
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={() => setShowProductSelector(true)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 16px',
                                            backgroundColor: '#1e40af',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Plus size={16} />
                                        Add Products
                                    </button>
                                </div>

                                {errors.items && (
                                    <p style={{
                                        fontSize: '12px',
                                        color: '#dc2626',
                                        marginBottom: '16px',
                                        margin: '0 0 16px 0'
                                    }}>
                                        {errors.items}
                                    </p>
                                )}

                                {formData.items.length > 0 ? (
                                    <div style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        overflow: 'hidden'
                                    }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                            <tr style={{ backgroundColor: '#f9fafb' }}>
                                                <th style={{
                                                    padding: '12px',
                                                    textAlign: 'left',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    Item
                                                </th>
                                                <th style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    textTransform: 'uppercase',
                                                    width: '100px'
                                                }}>
                                                    Qty
                                                </th>
                                                <th style={{
                                                    padding: '12px',
                                                    textAlign: 'right',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    textTransform: 'uppercase',
                                                    width: '120px'
                                                }}>
                                                    Unit Price
                                                </th>
                                                <th style={{
                                                    padding: '12px',
                                                    textAlign: 'right',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    textTransform: 'uppercase',
                                                    width: '120px'
                                                }}>
                                                    Total
                                                </th>
                                                <th style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    textTransform: 'uppercase',
                                                    width: '60px'
                                                }}>
                                                    Action
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {formData.items.map((item, index) => (
                                                <tr key={index} style={{
                                                    borderBottom: index < formData.items.length - 1 ? '1px solid #f3f4f6' : 'none'
                                                }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div>
                                                            <div style={{
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                color: '#1f2937',
                                                                marginBottom: '2px'
                                                            }}>
                                                                {item.description}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '12px',
                                                                color: '#6b7280'
                                                            }}>
                                                                #{item.itemNo}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                                            style={{
                                                                width: '60px',
                                                                padding: '6px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                textAlign: 'center'
                                                            }}
                                                            min="1"
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                                        <input
                                                            type="number"
                                                            value={item.unitPrice}
                                                            onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                            style={{
                                                                width: '80px',
                                                                padding: '6px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                textAlign: 'right'
                                                            }}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                              <span style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#1f2937'
                              }}>
                                {formatCurrency(item.totalPrice)}
                              </span>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveItem(index)}
                                                            style={{
                                                                padding: '4px',
                                                                backgroundColor: '#fef2f2',
                                                                color: '#dc2626',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{
                                        padding: '48px',
                                        textAlign: 'center',
                                        border: '2px dashed #d1d5db',
                                        borderRadius: '8px',
                                        color: '#6b7280'
                                    }}>
                                        <p style={{ margin: '0 0 16px 0' }}>No items added yet</p>
                                        <button
                                            type="button"
                                            onClick={() => setShowProductSelector(true)}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 16px',
                                                backgroundColor: '#1e40af',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Plus size={16} />
                                            Add Your First Product
                                        </button>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Summary Sidebar - UNCHANGED */}
                    <div style={{
                        flex: '1',
                        backgroundColor: '#f9fafb',
                        padding: '24px',
                        borderLeft: '1px solid #e5e7eb'
                    }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '20px'
                        }}>
                            Quotation Summary
                        </h3>

                        <div style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                            }}>
                                <span style={{ fontSize: '14px', color: '#6b7280' }}>Items:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {formData.items.length}
                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                            }}>
                                <span style={{ fontSize: '14px', color: '#6b7280' }}>Subtotal:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {formatCurrency(calculateTotal())}
                </span>
                            </div>

                            <div style={{
                                borderTop: '1px solid #e5e7eb',
                                paddingTop: '12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  Total:
                </span>
                                <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                  {formatCurrency(calculateTotal())}
                </span>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#1f2937',
                                marginBottom: '12px'
                            }}>
                                Quotation Details
                            </h4>

                            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
                                <p style={{ margin: '0 0 8px 0' }}>
                                    <strong>Client:</strong> {formData.clientName || 'Not specified'}
                                </p>
                                {formData.companyName && (
                                    <p style={{ margin: '0 0 8px 0' }}>
                                        <strong>Company:</strong> {formData.companyName}
                                    </p>
                                )}
                                <p style={{ margin: '0 0 8px 0' }}>
                                    <strong>Project:</strong> {formData.projectName || 'Not specified'}
                                </p>
                                <p style={{ margin: '0 0 8px 0' }}>
                                    <strong>Validity:</strong> {formData.validityDays} days
                                </p>
                                <p style={{ margin: 0 }}>
                                    <strong>Status:</strong> {formData.status}
                                </p>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            flexDirection: 'column'
                        }}>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    backgroundColor: isSubmitting ? '#9ca3af' : '#1e40af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isSubmitting ? 'Saving...' : (quotation ? 'Update Quotation' : 'Create Quotation')}
                            </button>

                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    </div>
           </div>

           {/* Product Selector Modal */}
           {showProductSelector && (
               <ProductSelectorModal
                   products={products}
                   productCategories={productCategories}
                   onClose={() => setShowProductSelector(false)}
                   onAddProducts={handleAddProducts}
               />
           )}

           {/* NEW: Client Creation Modal */}
           {showNewClientModal && (
               <NewClientModal
                   onClose={() => {
                       setShowNewClientModal(false);
                       setQuickCreateClientName('');
                   }}
                   onSave={handleClientCreated}
                   initialName={quickCreateClientName}
                   isQuickCreate={true} // This shows a simplified version
               />
           )}
       </div>
   );
};

export default NewQuotationModal;